import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { useWorkspace } from '../../context/WorkspaceContext';
import useDashboardSection from './project/useDashboardSection';
import DashboardHeader from './project/DashboardHeader';
import KpiStrip from './project/KpiStrip';
import LatestExecution from './project/LatestExecution';
import QualityTrends from './project/QualityTrends';
import FailuresTable from './project/FailuresTable';
import ReleaseReadiness from './project/ReleaseReadiness';
import SuiteHealth from './project/SuiteHealth';
import RecentRuns from './project/RecentRuns';
import InsightsPanel from './project/InsightsPanel';
import { RANGE_OPTIONS } from './project/format';
import '../../styles/rt2-project-dashboard.css';

const DEFAULT_RANGE = '7d';
const FILTER_KEYS = ['range', 'from', 'to', 'env'];
const VALID_RANGES = new Set(RANGE_OPTIONS.map((o) => o.id));
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readFilters(searchParams) {
  const range = searchParams.get('range');
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const env = searchParams.get('env');
  return {
    range: VALID_RANGES.has(range) ? range : DEFAULT_RANGE,
    from: from && ISO_DATE.test(from) ? from : null,
    to: to && ISO_DATE.test(to) ? to : null,
    env: env && /^\d+$/.test(env) ? env : null,
  };
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Selected-project quality dashboard. Every section loads (and fails) independently
 * from `/dashboard_data/project/*`; nothing here is sample data.
 */
const ProjectOverview = ({ projectName, navigateTo }) => {
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const { currentWorkspace } = useWorkspace();
  const projectId = getProjectIdFromURL();
  const workspaceId = getWorkspaceIdFromURL();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);
  const customIncomplete = filters.range === 'custom' && !(filters.from && filters.to);

  const scopeParams = useMemo(() => {
    const p = { project_id: projectId };
    if (workspaceId) p.workspace_id = workspaceId;
    if (filters.env) p.environment_id = filters.env;
    return p;
  }, [projectId, workspaceId, filters.env]);

  const baseParams = useMemo(() => {
    const p = { ...scopeParams, range: filters.range };
    if (filters.range === 'custom' && filters.from && filters.to) {
      p.start_date = filters.from;
      p.end_date = filters.to;
    }
    return p;
  }, [scopeParams, filters.range, filters.from, filters.to]);

  const enabled = Boolean(projectId) && !customIncomplete;
  const summary = useDashboardSection('summary', baseParams, { enabled });
  const [pollMs, setPollMs] = useState(null);
  const latest = useDashboardSection('latest_execution', scopeParams, { enabled: Boolean(projectId), pollMs });
  const readiness = useDashboardSection('release_readiness', baseParams, { enabled });
  const suiteHealth = useDashboardSection('suite_health', baseParams, { enabled });
  const recent = useDashboardSection('recent_runs', baseParams, { enabled });
  const [failuresRequest, setFailuresRequest] = useState(null);
  const failuresRef = useRef(null);

  const reloadAll = useCallback(() => {
    summary.reload();
    latest.reload();
    readiness.reload();
    suiteHealth.reload();
    recent.reload();
    setFailuresRequest({ refresh: Date.now() });
  }, [summary, latest, readiness, suiteHealth, recent]);

  // Poll only while a run is in progress; refresh dependent sections once it finishes.
  const wasRunning = useRef(false);
  useEffect(() => {
    const running = latest.data?.run?.state === 'running';
    setPollMs(running && latest.data?.poll_interval_seconds ? latest.data.poll_interval_seconds * 1000 : null);
    if (wasRunning.current && !running && latest.data) {
      summary.reload({ silent: true });
      readiness.reload({ silent: true });
      suiteHealth.reload({ silent: true });
      recent.reload({ silent: true });
      setFailuresRequest({ refresh: Date.now() });
    }
    wasRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest.data]);

  const updateFilters = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => {
            if (value == null || value === '') next.delete(key);
            else next.set(key, value);
          });
          if (patch.range && patch.range !== 'custom') {
            next.delete('from');
            next.delete('to');
          }
          if (next.get('range') === DEFAULT_RANGE) next.delete('range');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        FILTER_KEYS.forEach((k) => next.delete(k));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const openFailures = useCallback((kind) => {
    setFailuresRequest({ kind, at: Date.now() });
    failuresRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, []);

  const anyRefreshing = [summary, latest, readiness, suiteHealth, recent].some((s) => s.refreshing);
  const summaryData = summary.data;

  return (
    <div className="pqd">
      <DashboardHeader
        workspaceName={currentWorkspace?.workspace_name}
        projectName={projectName}
        project={summaryData?.project}
        environments={summaryData?.environments}
        filters={filters}
        onFiltersChange={updateFilters}
        onReset={resetFilters}
        isDefaultFilters={filters.range === DEFAULT_RANGE && !filters.env}
        onRefresh={reloadAll}
        refreshing={anyRefreshing}
        generatedAt={summaryData?.generated_at}
        navigateTo={navigateTo}
      />

      {customIncomplete && (
        <p className="pqd-hint" role="status">Choose a start and end date, then select Apply.</p>
      )}

      <KpiStrip
        summary={summaryData}
        loading={summary.loading}
        error={summary.error}
        onRetry={summary.retry}
        range={filters.range}
        onOpenFailures={openFailures}
        navigateTo={navigateTo}
      />

      <LatestExecution section={latest} navigateTo={navigateTo} />

      <QualityTrends summary={summaryData} loading={summary.loading} error={summary.error} onRetry={summary.retry} />

      <div ref={failuresRef} className="pqd-anchor">
        {enabled && (
          <FailuresTable baseParams={baseParams} request={failuresRequest} navigateTo={navigateTo} projectId={projectId} />
        )}
      </div>

      <div className="pqd-grid pqd-grid--2">
        <ReleaseReadiness section={readiness} navigateTo={navigateTo} onOpenFailures={openFailures} projectId={projectId} />
        <SuiteHealth section={suiteHealth} navigateTo={navigateTo} projectId={projectId} />
      </div>

      <div className="pqd-grid pqd-grid--wide-left">
        <RecentRuns section={recent} navigateTo={navigateTo} />
        <InsightsPanel
          summary={summaryData}
          loading={summary.loading}
          error={summary.error}
          onRetry={summary.retry}
          navigateTo={navigateTo}
          onOpenFailures={openFailures}
        />
      </div>
    </div>
  );
};

export default ProjectOverview;
